import { useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { useGetCheckOptionsQuery } from '../../store/api/apiSlice';
import type { CheckOption } from '../../types';
import {
    FALLBACK_CHECK_OPTIONS,
    medicalTestList,
    planAddons,
    planFeatures,
    priceSelection,
    visibleCheckOptions,
} from '../../utils/checkOptions';
import { stashSelection } from '../../utils/planSelection';
import WhatsAppChatButton from './WhatsAppChatButton';

function formatNaira(price: number): string {
    return new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency: 'NGN',
        minimumFractionDigits: 0,
    }).format(price);
}

// Who each tier is best suited for. Display-only — the API doesn't send this,
// so it's keyed by plan id (with name as a fallback).
const RECOMMENDED_FOR: Record<string, string> = {
    standard: 'Recommended for domestic staff',
    enhanced: 'Recommended for domestic and corporate employees',
    pro: 'Recommended for senior executives',
};

function recommendedFor(option: CheckOption): string | undefined {
    return RECOMMENDED_FOR[option.id] ?? RECOMMENDED_FOR[option.name.toLowerCase()];
}

// Deduped union of every medical test across the category panels.

function PricingCard({ option, onProceed }: { option: CheckOption; onProceed: () => void }) {
    const isDark = option.popular;
    const recommendation = recommendedFor(option);
    const medicalTests = medicalTestList(option.medicalAnalysis);
    // The plan's priced lines, and the extras it can be topped up with.
    const features = planFeatures(option);
    const addons = planAddons(option);
    // Everything starts ticked: the whole plan is what we want them on, and it
    // is the cheapest way to get these checks.
    const [selectedFeatures, setSelectedFeatures] = useState<string[]>(() => features.map((f) => f.id));
    const [selectedAddons, setSelectedAddons] = useState<string[]>([]);

    // Mirrors the server, so the figure here is what checkout will charge.
    const quote = priceSelection(option, selectedFeatures, selectedAddons);
    const displayPrice = quote.total;
    const toggleFeature = (id: string) =>
        setSelectedFeatures((prev) => (prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]));
    const toggleAddon = (id: string) =>
        setSelectedAddons((prev) => (prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]));
    const takeWholePlan = () => setSelectedFeatures(features.map((f) => f.id));
    // Ordered by the plan so the payload matches what the server stores.
    const featureIds = features.filter((f) => selectedFeatures.includes(f.id)).map((f) => f.id);
    const addonIds = addons.filter((a) => selectedAddons.includes(a.id)).map((a) => a.id);
    const costsMoreThanPlan = !quote.isWholePlan && quote.featuresTotal > option.price;

    // Checkout is behind auth and the signup redirect drops in-memory state, so
    // the choice is stashed and rehydrated once they are signed in.
    const handleProceed = () => {
        if (features.length > 0) {
            stashSelection({
                checkType: option.id,
                features: featureIds,
                addons: addonIds,
                planName: option.name,
                pricePerSubject: quote.total,
            });
        }
        onProceed();
    };

    return (
        <div
            className={`flex-1 ${isDark ? 'bg-[#14171A]' : 'bg-white'} rounded-2xl lg:rounded-3xl p-6 md:p-8 flex flex-col`}
        >
            <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'} text-sm font-medium mb-2`}>{option.name}</p>
            {recommendation && <p className="text-[#FF6B35] text-xs font-semibold mb-3">{recommendation}</p>}
            <p className="text-[#FF6B35] text-3xl md:text-4xl font-bold mb-1">{formatNaira(displayPrice)}</p>
            {/* The plan is worth stating against what these checks cost separately. */}
            {quote.isWholePlan && option.separatePrice && option.wholePlanSaving ? (
                <p className={`${isDark ? 'text-gray-400' : 'text-gray-500'} mb-6 text-xs`}>
                    <span className="line-through">{formatNaira(option.separatePrice)}</span> bought separately, saving{' '}
                    <span className="font-semibold text-[#FF6B35]">{formatNaira(option.wholePlanSaving)}</span>
                </p>
            ) : (
                <div className="mb-6" />
            )}
            {/* Every line priced and tickable. Untick to drop a check; the price
                above follows, and the prompt below warns when dropping one has
                cost more than it saved. */}
            {features.length > 0 && (
                <p className={`mb-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    All included |{' '}
                    <span className={isDark ? 'font-medium text-gray-200' : 'font-medium text-gray-700'}>
                        tap any line to remove it
                    </span>
                </p>
            )}
            <ul className="space-y-1 mb-4 flex-1">
                {features.length > 0
                    ? features.map((feature) => {
                          const isOn = selectedFeatures.includes(feature.id);
                          return (
                              <li key={feature.id}>
                                  <button
                                      type="button"
                                      onClick={() => toggleFeature(feature.id)}
                                      aria-pressed={isOn}
                                      className={`flex w-full cursor-pointer items-start gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors ${
                                          isOn
                                              ? isDark
                                                  ? 'hover:bg-white/10'
                                                  : 'hover:bg-black/5'
                                              : isDark
                                                ? 'opacity-50 hover:opacity-80 hover:bg-white/10'
                                                : 'opacity-50 hover:opacity-80 hover:bg-black/5'
                                      }`}
                                  >
                                      {/* A bordered square, not a soft circle: it has
                                          to read as a checkbox at a glance, or
                                          nobody realises the lines are optional. */}
                                      <div
                                          className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 mt-0.5 transition-colors ${
                                              isOn
                                                  ? 'border-[#FF6B35] bg-[#FF6B35]'
                                                  : isDark
                                                    ? 'border-gray-500 bg-transparent'
                                                    : 'border-gray-300 bg-white'
                                          }`}
                                      >
                                          {isOn && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                                      </div>
                                      <span
                                          className={`${isDark ? 'text-gray-300' : 'text-gray-700'} flex-1 text-sm ${isOn ? '' : 'line-through'}`}
                                      >
                                          {feature.label}
                                      </span>
                                      <span
                                          className={`${isDark ? 'text-gray-500' : 'text-gray-400'} shrink-0 text-xs tabular-nums`}
                                      >
                                          {formatNaira(feature.price)}
                                      </span>
                                  </button>
                              </li>
                          );
                      })
                    : /* A response that predates priced features: nothing to pick. */
                      option.features.map((label, i) => (
                          <li key={i} className="flex items-start gap-2.5 px-2 py-1.5">
                              <div
                                  className={`w-5 h-5 ${isDark ? 'bg-[#FF6B35]/20' : 'bg-[#FF6B35]/10'} rounded-full flex items-center justify-center shrink-0 mt-0.5`}
                              >
                                  <Check className="w-3 h-3 text-[#FF6B35]" />
                              </div>
                              <span className={`${isDark ? 'text-gray-300' : 'text-gray-700'} flex-1 text-sm`}>
                                  {label}
                              </span>
                          </li>
                      ))}
            </ul>

            {/* Dropping a line loses the whole plan saving, which often costs
                more than the line itself. Say so here, not at the payment screen. */}
            {!quote.isWholePlan && features.length > 0 && (
                <button
                    type="button"
                    onClick={takeWholePlan}
                    className={`mb-5 flex items-start gap-2 rounded-xl border border-dashed px-3 py-2.5 text-left text-xs ${
                        costsMoreThanPlan
                            ? 'border-[#FF6B35] bg-[#FF6B35]/10'
                            : isDark
                              ? 'border-white/20'
                              : 'border-gray-200'
                    }`}
                >
                    <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                        {costsMoreThanPlan ? (
                            <>
                                That is {formatNaira(quote.featuresTotal - option.price)} more than the full{' '}
                                {option.name} plan.{' '}
                                <span className="font-semibold text-[#FF6B35]">Take everything</span>
                            </>
                        ) : (
                            <>
                                Taking every line saves {option.wholePlanDiscountPercent ?? 9}%.{' '}
                                <span className="font-semibold text-[#FF6B35]">Restore the full plan</span>
                            </>
                        )}
                    </span>
                </button>
            )}

            {/* Add-ons — tick any, or take the whole bundle at a discount */}
            {addons.length > 0 && (
                <div className="mb-5 flex flex-col gap-2">
                    <p
                        className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}
                    >
                        Add-ons
                    </p>

                    {addons.map((addon) => {
                        const isOn = selectedAddons.includes(addon.id);
                        return (
                            <div key={addon.id} className="flex flex-col gap-1.5">
                                <button
                                    type="button"
                                    onClick={() => toggleAddon(addon.id)}
                                    className={`flex items-start gap-3 rounded-xl border px-3 py-3 text-left transition-colors ${
                                        isOn
                                            ? 'border-[#FF6B35] bg-[#FF6B35]/10'
                                            : isDark
                                              ? 'border-white/20 hover:border-white/40'
                                              : 'border-gray-200 hover:border-gray-300'
                                    }`}
                                >
                                    <span
                                        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border ${
                                            isOn
                                                ? 'border-[#FF6B35] bg-[#FF6B35]'
                                                : isDark
                                                  ? 'border-white/40'
                                                  : 'border-gray-300'
                                        }`}
                                    >
                                        {isOn && <Check className="h-3.5 w-3.5 text-white" />}
                                    </span>
                                    <span className="flex flex-col gap-0.5">
                                        <span
                                            className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}
                                        >
                                            {addon.name} (+{formatNaira(addon.price)})
                                        </span>
                                        {addon.description && (
                                            <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                                {addon.description}
                                            </span>
                                        )}
                                    </span>
                                </button>

                                {/* Which tests the medical panel covers */}
                                {addon.id === 'medical_analysis' && medicalTests.length > 0 && (
                                    <div className="flex flex-wrap gap-1.5">
                                        {medicalTests.map((test) => (
                                            <span
                                                key={test}
                                                className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium ${
                                                    isDark ? 'bg-white/10 text-gray-300' : 'bg-gray-100 text-gray-600'
                                                }`}
                                            >
                                                {test}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            <button
                type="button"
                onClick={handleProceed}
                disabled={features.length > 0 && featureIds.length === 0}
                className={`w-full ${isDark ? 'bg-[#FF6B35] hover:bg-[#ff5722]' : 'bg-black hover:bg-gray-900'} text-white py-3 rounded-full text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed`}
            >
                Proceed
            </button>
        </div>
    );
}

interface PricingCtaSectionProps {
    onProceed: () => void;
    onContactAssociate: () => void;
}

export default function PricingCtaSection({ onProceed, onContactAssociate }: PricingCtaSectionProps) {
    const { data: checkOptions, isLoading } = useGetCheckOptionsQuery();

    // Show every customer-facing tier (Basic, Standard, Professional); the internal
    // "Test (Live Payment Check)" plan is filtered out.
    const displayOptions = visibleCheckOptions(
        checkOptions && checkOptions.length > 0 ? checkOptions : FALLBACK_CHECK_OPTIONS,
    );

    return (
        <section className="w-full bg-[#F5F5F5] px-4 md:px-6 lg:px-10 py-16 md:py-20 lg:py-24">
            <div className="w-full mx-auto">
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-8 md:mb-12">
                    Our Pricing Options
                </h2>

                {/* Pricing tiers */}
                {isLoading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="w-8 h-8 text-[#FF6B35] animate-spin" />
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                        {displayOptions.map((option) => (
                            <PricingCard key={option.id} option={option} onProceed={onProceed} />
                        ))}
                    </div>
                )}

                {/* CTA banner */}
                <div className="mt-8 md:mt-10 bg-[#14171A] rounded-2xl lg:rounded-3xl p-8 md:p-10 lg:p-12 flex flex-col lg:flex-row lg:items-center justify-between gap-8 overflow-hidden relative">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-[#FF6B35]/5 rounded-full blur-3xl" />

                    <div className="relative z-10 max-w-[560px]">
                        <h3 className="text-white text-2xl md:text-3xl lg:text-4xl font-bold mb-4 md:mb-6 leading-tight">
                            Ready to verify? Order your checks now.
                        </h3>
                        <p className="text-gray-400 text-sm md:text-base leading-relaxed mb-8 max-w-[440px]">
                            Get started today with YourChecka and ensure your hiring decisions are backed by thorough,
                            accurate background verification.
                        </p>

                        <div className="flex flex-col sm:flex-row flex-wrap gap-3 md:gap-4">
                            <button
                                type="button"
                                onClick={onProceed}
                                className="bg-[#FF6B35] text-white px-6 py-3 rounded-full text-sm font-medium hover:bg-[#ff5722] transition-colors whitespace-nowrap"
                            >
                                Run a Background Check
                            </button>
                            <WhatsAppChatButton className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-[#1EBE5B] whitespace-nowrap focus:outline-none focus-visible:ring-2 focus-visible:ring-[#25D366] focus-visible:ring-offset-2 focus-visible:ring-offset-[#14171A]" />
                            {/* Contact our Associate — hidden for now
                            <button
                                type="button"
                                onClick={onContactAssociate}
                                className="border border-[#FF6B35] text-[#FF6B35] px-6 py-3 rounded-full text-sm font-medium hover:bg-[#FF6B35]/10 transition-colors whitespace-nowrap"
                            >
                                Contact our Associate
                            </button>
                            */}
                        </div>
                    </div>

                    <div className="relative z-10 shrink-0 flex lg:justify-end">
                        <div className="w-48 h-32 md:w-64 md:h-40 bg-gradient-to-t from-[#FF6B35]/10 to-transparent rounded-2xl flex items-end justify-center overflow-hidden">
                            <svg viewBox="0 0 200 120" fill="none" className="w-full h-full opacity-30">
                                <ellipse cx="100" cy="115" rx="60" ry="8" fill="#FF6B35" opacity="0.3" />
                                <rect x="70" y="80" width="60" height="5" rx="2" fill="#FF6B35" opacity="0.5" />
                                <rect x="75" y="55" width="50" height="25" rx="3" fill="#FF6B35" opacity="0.4" />
                                <circle cx="100" cy="40" r="15" fill="#FF6B35" opacity="0.4" />
                            </svg>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
